// **************************** React Libary Imports ****************************
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import Skeleton from "react-loading-skeleton";
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

// *************************** icon ******************************
import { BsArrowLeft } from 'react-icons/bs';
import { FaHistory } from "react-icons/fa";

// **************************** Images Imports **************************
import DefaultProfileImage from "../../../assets/images/ProfileImage.png";
import { WordTitle } from "../../../utils/commonFunction";

// **************************** Redux Imports ****************************
import {
  getAllergyById,
  getPatientLookupById,
  getMedicationById,
  getObservationById,
  getImmuzationsById,
} from "../../../api/list";

// **************************** Component Imports ****************************
import CommonHeader from "../../../components/common-header";
import Table from "../../../components/table/table";
import Popup from "./popup";
import ExpiredRateLimit from "./expired-rate-limit"
import NotesTab from "./notes-tab";

// **************************** Utils Imports ****************************
import { CalculateAge } from "../../../utils";
import { IoCopyOutline } from "react-icons/io5";
import { BsCheckCircleFill } from "react-icons/bs";
import "../../../components/table/table.css";

const Index = () => {
  // **************************** Hooks Imports ****************************
  const { id }: any = useParams();
  const navigate = useNavigate();
  const [getPatientLookupData, setGetPatientLookup] = useState<any>(null);
  const [, setProfileImage] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<any>("medications");
  const [allergy, setAllergy] = useState<any>([]);
  const [allergyRateLimit, setAllergyRateLimit] = useState<boolean>(false);
  const [medications, setMedications] = useState<any>([]);
  const [medicationsRateLimit, setMedicationsRateLimit] = useState<boolean>(false);
  const [immuzations, setImmuzations] = useState<any>([]);
  const [immuzationsRateLimit, setImmuzationsRateLimit] = useState<boolean>(false);
  const [observation, setObservation] = useState<any>(null);
  const [observationRateLimit, setObservationRateLimit] = useState<boolean>(false);
  const [copied, setCopied] = useState<any>(null);
  const [loader, setLoader] = useState<Boolean>(false);
  const [loaderMedications, setLoaderMedications] = useState<Boolean>(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [status, setStatus] = useState("all");
  const [totalResults, setTotalResults] = useState(0);
  const [singleCopy, setSingleCopy] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [sorting, setSorting] = useState<any>({ created_at: "asc" });
  const [observationCopy, setObservationCopy] = useState<boolean>(false);
  const [ , setInitalLoad] = useState<boolean>(false);

  // **************************** Functions Imports ****************************
  const typeOrder: any = {
    BP: "bloodPressure",
    Temp: "temperature",
    Pulse: "heartrate",
    Weight: "weight",
    Resp: "respirations",
    BS: "bloodSugar",
    O2: "oxygenSaturation",
    Pain: "painLevel",
    Height: "height",
  };

  /**
   * @function getMedications
   * @param id Patient Id
   * @returns medications Details
   * @description Get Medications
   */
  const getMedications = (
    id: string | number,
    load: boolean,
    sort: any = {},
    pageNumber?: number,
    selectedStatus?: string
  ) => {
    setMedicationsRateLimit(false)
    let waitForLoadingCount = 0;
    const currectPage = pageNumber || page;
    const sort_key =
      Object.keys(sort).length !== 0
        ? Object.keys(sort)[0]
        : Object.keys(sorting).length !== 0
        ? Object.keys(sorting)[0]
        : "created_at";
    const sort_dir = sort[sort_key] || sorting[sort_key] || "asc";
    if (currectPage === 1) {
      setLoader(true);
      setLoaderMedications(true)
    }
    const fetchMedication = () => {
      getMedicationById(id, {
        page: currectPage,
        limit,
        status: selectedStatus ? selectedStatus : status,
        sort_by: sort_key,
        sort_dir: sort_dir,
      }).then((res) => {
        if (!res?.success && res?.result?.loading && waitForLoadingCount === 0) {
          waitForLoadingCount += 1;
          setTimeout(fetchMedication, 5000);
        } else if (res?.result?.data) {
          if (currectPage === 1) {
            setMedications(res.result.data || []);
          } else {
            setMedications((prev: any) => [
              ...prev,
              ...(res.result.data || []),
            ]);
          }
          setPage(currectPage);
          setLimit(res?.result?.limit || 15);
          setTotalResults(res?.result?.total_pages || 0);
          setSorting({[sort_key]:sort_dir})
        } else if (res?.detail?.success && res?.detail?.result?.is_api_limit_expired){
          setMedicationsRateLimit(true)
        }
        setLoader(false);
        setLoaderMedications(false);
      });
    };
    fetchMedication();
  };

  /**
   * @function getAllergy
   * @param id Patient Id
   * @returns Allergy Details
   * @description Get Allergy
   */
  const getAllergy = (id: string | number) => {
    setAllergyRateLimit(false)
    let waitForLoadingCount = 0;
    const fetchAllergy = () => {
      getAllergyById(id).then((res) => {
        if (!res?.success && res?.result?.loading && waitForLoadingCount === 0) {
          waitForLoadingCount += 1;
          setTimeout(fetchAllergy, 5000);
        } else if (res?.result?.data) {

          setAllergy(res?.result?.data || []);
          setLoader(false);
        } else if (res?.detail?.success && res?.detail?.result?.is_api_limit_expired){
          setAllergyRateLimit(true)
          setLoader(false);
        } else {
          setLoader(false);
        }
      });
    };

    fetchAllergy();
  };

  /**
   * @function getPatientLookup
   * @param id Patient Id
   * @returns Patient Details
   * @description Get Patient Details
   */
  const getPatientLookup = (id: string | number) => {
    getPatientLookupById(id).then((res) => {
      if (res?.result?.data) {
        setGetPatientLookup(res?.result);
        if (res?.result?.data?.photo_data) {
          const base64Image: any = `data:image/png;base64,${res.result.data.photo_data}`;
          setProfileImage(base64Image);
        } else {
          setProfileImage(DefaultProfileImage);
        }
      } else{
        if (res?.detail?.message === 'Patient is not found'){
          toast.error("Patient is not found in our system");
          setTimeout(() => { 
            navigate('/patient-lookup');
          },5000)
        }
      }
    });
  };

  /**
   * @function getObservationDetails
   * @param id Patient Id
   * @returns Vitals Details
   * @description Get Vitals Information
   */

  
 const getObservationDetails = (id: string | number) => {
   setObservationRateLimit(false)
   let waitForLoadingCount = 0;
   const fetchObservation = () => {
     getObservationById(id).then((res: any) => {
       if (!res?.success && res?.result?.loading && waitForLoadingCount === 0) {
          waitForLoadingCount += 1;
          setTimeout(fetchObservation, 5000); // Retry after 5 second
       } else if (res?.result?.data){
        const filteredOrderedData = res?.result?.data
              .map((item: any) => {
                const match = Object.entries(typeOrder).find(
                  ([key, value]) => value === item.type
                );
                return match ? { ...item, observationType: match[0] } : null;
              })
              .filter(Boolean);
            setObservation(filteredOrderedData);
            setObservationCopy(res?.result?.have_observations);
        } else if (res?.detail?.success && res?.detail?.result?.is_api_limit_expired){
          setObservationRateLimit(true)
          setObservation([]);
          setObservationCopy(false);
        } else {
          setObservation([]);
          setObservationCopy(false);
        }
      });
    };

    fetchObservation();
  };

  /**
   * @function getImmuzations
   * @param id Patient Id
   * @returns Immunization Details
   * @description Get Immunization Informations
   */
  const getImmuzations = (
    id: string | number,
    page?: number,
    limit?: number,
    status?: string
  ) => {
    setImmuzationsRateLimit(false)
    let waitForLoadingCount = 0;
    const fetchImmuzations = () => {
      getImmuzationsById(id, {
        page,
        limit,
        status,
      }).then((res) => {
        if (
          !res?.success &&
          res?.result?.loading &&
          waitForLoadingCount === 0 &&
          page === 1
        ) {
          waitForLoadingCount += 1;
          setTimeout(fetchImmuzations, 5000);
        } else if (res?.result?.data) {
          setImmuzations(res.result.data || []);
          setPage(res?.result?.page || 1);
          setLimit(res?.result?.limit || 15);
          setTotalResults(res?.result?.total_pages || 0);
        } if (res?.detail?.success && res?.detail?.result?.is_api_limit_expired){
          setImmuzationsRateLimit(true)
        }
        setLoader(false);
      });
    };
    fetchImmuzations();
  };

  useEffect(() => {
    setLoader(true);
    setInitalLoad(true);
    getPatientLookup(id);
    setStatus('active')
    getMedications(id, true, {}, page, 'active');
    getObservationDetails(id);
    setLoader(false);
    setInitalLoad(false);
  }, []);
  /**
   * @function handleNextPage
   * @description Handle Next Page
   * @returns Null
   * @description Handle Next Page
   * */
  const handleNextPage = () => {
    setPage(page + 1);
    getMedications(id, true, {}, page + 1);
  };

  /**
   * @function formatData
   * @param data all vitals object
   * @returns formated vitals string informations
   * @description Get formated vitals
   */
  const formatData = (data: any) => {
    if (data?.type === "bloodPressure") {
      if (
        data?.systolicValue &&
        data?.diastolicValue !== "0" &&
        data?.diastolicValue &&
        data?.systolicValue !== "0"
      )
        return `${
          data?.systolicValue && data?.diastolicValue !== "0"
            ? data?.systolicValue
            : ""
        }/${
          data?.diastolicValue && data?.systolicValue !== "0"
            ? data?.diastolicValue
            : ""
        } ${data?.unit ?? ""}`;
      else return "-";
    } else if (data?.value && data?.unit) {
      return `${data?.value} ${data?.unit}`;
    } else if(data?.value){
      return `${data?.value}`;
    }else {
      return "-";
    }
  };

  /**
   * @function handleTabClick
   * @param tab strings
   * @returns Null
   * @description Updates active tab
   */
  const handleTabClick = (tab: any) => {
    setActiveTab(tab);
    if (tab === "medications") {
      setStatus("active");
      setLoader(true);
      getMedications(id, true, {}, 1, 'active');
    } else if (tab === "allergy") {
      setStatus("all");
      setLoader(true);
      getAllergy(id);
    } else if (tab === "vitals") {
      getObservationDetails(id);
    } else if (tab === "immunization") {
      setLoader(true);
      getImmuzations(id, page, limit);
    }
  };

  /**
   * @function handleCopy
   * @param value string
   * @returns Null
   * @description Handle Copy
   */
  const handleCopy = (value: string) => {
    setCopied(value);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  /**
   * @function handleStatusFilter
   * @param status string
   * @returns Null
   * @description Handle Status Filter
   */
  const handleStatusFilter = (status: string) => {
    setStatus(status);
    if (activeTab === "medications") {
      setLoader(true);
      setLoaderMedications(true)
      getMedicationById(id, {
        status: status,
        limit: 15,
      }).then((res) => {
        setMedications(res?.result?.data || []);
        setPage(res?.result?.page || 1);
        setTotalResults(res?.result?.total_pages || 0);
        setLimit(res?.result?.limit || 15);
      });
      setLoader(false);
      setLoaderMedications(false)
    } else if (activeTab === "allergy") {
      getAllergyById(id, {
        type: status === "all" ? "" : status,
        limit: 15,
      }).then((res) => {
        setAllergy(res?.result?.data || []);
        setPage(res?.result?.page || 1);
        setTotalResults(res?.result?.total_pages || 0);
        setLimit(res?.result?.limit || 15);
      });
    }
  };

  /**
   * @function handleSingleCopy
   * @param data object
   * @param index number
   * @returns Null
   * @description Handle Single Copy
   */
  const handleSingleCopy = (data: any, index: number) => {
    setSingleCopy(index);
    const header = `Consent Confirmed Date | Immunization | Description `;
    const separator = `________________________________________________________`;
    const rows = `• ${data?.consentConfirmedDate} | ${data?.immunization} | ${data?.cvxDescription} `
    navigator.clipboard.writeText(
      `${header}\n${separator}\n${rows}`
    );
    setTimeout(() => {
      setSingleCopy(null);
    }, 2000);
  };

  const medicationClipBoard = (medications: any[]) => {
      if (!medications || medications.length === 0) {
        return "No Results Found";
      }
   
      const header = `Description | Strength Unit | Directions | Schedule `;
      const separator = `____________________________________________________________`;
   
      const rows = medications.map((item) => {
        const description = item?.description || "";
        const strength = item?.strength || "";
        const strengthUOM = item?.strengthUOM || "";
        const directions = item?.directions || "";
        const schedule = item?.schedule?.length > 0 ? item.schedule.join(", ") : "";
   
        return `• ${description} | ${strength}${strengthUOM} | ${directions} | ${schedule}`;
      });
   
      return `${header}\n${separator}\n${rows.join("\n")}`;
    };

  const allergyClipBoard = (allergy: any[]) => {
    if (!allergy || allergy.length === 0) {
      return "No Results Found";
    }
 
    const header = `Category | Allergen | Reaction | Severity`;
    const separator = `__________________________________`;
 
    const rows = allergy.map((item) => {
      const category = item?.category || "";
      const allergen = item?.allergen || "";
      const reaction = item?.reaction?.length > 0 ? item.reaction?.join(",") : "";
      const severity = item?.severity || "";
 
      return `• ${category} | ${allergen} | ${reaction} | ${severity}`;
    });
 
    return `${header}\n${separator}\n${rows.join("\n")}`;
  };


  const immuzationClipBoard = (immuzations: any[]) => {
    if (!immuzations || immuzations.length === 0) {
      return "No Results Found";
    }
 
    const header = `Consent Confirmed Date | Immunization | Description `;
    const separator = `________________________________________________________`;
 
    const rows = immuzations.map((item) => {
      const consentConfirmedDate = item?.consentConfirmedDate || "";
      const immunization = item?.immunization || "";
      const cvxDescription = item?.cvxDescription || "";
 
      return `• ${consentConfirmedDate} | ${immunization} | ${cvxDescription} `;
    });
 
    return `${header}\n${separator}\n${rows.join("\n")}`;
  };


  return (
    <div className="w-full p-[10px] pl-[2px] pr-[2px]">
      <div className="pt-[16px]  pl-[2px] pr-[2px] bg-white rounded-[12px]">
        <CommonHeader
            heading="Patient Details"
            description="View all the details of your patients here"
          />

      <div className="grid grid-cols-[34%,64%] w-full gap-[2%]">
        <div className="">
            <div className="flex">
              <Link
                  to="/patient-lookup"
                  className="flex items-center justify-center p-2 border border-[#dddddd] bg-white rounded-lg"
                >
                  <BsArrowLeft className=" w-[18px] h-[18px] text-primary-light stroke-1" /> 
              </Link>
              <div className="ms-2">
                <p className="font-normal text-black text-lg">{getPatientLookupData?.data?.lastName || ""}</p>
                <p className="font-normal text-xs text-[#202A2B]">{getPatientLookupData?.data?.firstName || ""}</p>
              </div>
            </div>

            <hr className="my-4"/>
            <div>
                <div className="flex items-center justify-between my-3">
                    <div className="font-medium  text-xs text-[#AAAAAA]">Status: </div>
                    <div className="font-medium text-primary-light text-xs">{getPatientLookupData?.data?.patientStatus || "-"}</div>
                </div>
                <div className="flex items-center justify-between my-3">
                    <div className="font-medium  text-xs text-[#AAAAAA]">DOB: </div>
                    <div className="font-medium text-primary-light text-xs">{getPatientLookupData?.data?.birthDate || "-"}</div>
                </div>
                <div className="flex items-center justify-between my-3">
                    <div className="font-medium  text-xs text-[#AAAAAA]">Location:</div>
                    <div className="font-medium text-primary-light text-xs">
                      {getPatientLookupData?.data?.floorDesc &&
                       getPatientLookupData?.data?.roomDesc &&
                       getPatientLookupData?.data?.bedDesc
                      ? `${getPatientLookupData?.data?.floorDesc ?? ""} ${
                          getPatientLookupData?.data?.roomDesc ?? ""
                        } - ${getPatientLookupData?.data?.bedDesc ?? ""}`
                      : "-"}
                    </div>
                </div>
                <div className="flex items-center justify-between my-3">
                    <div className="font-medium  text-xs text-[#AAAAAA]">Facility:</div>
                    <div className="font-medium text-primary-light text-xs">
                       {WordTitle(getPatientLookupData?.data?.location) || "-"}
                    </div>
                </div>
                <div className="flex items-center justify-between my-3">
                    <div className="font-medium  text-xs text-[#AAAAAA]">Gender:</div>
                    <div className="font-medium text-primary-light text-xs">
                       {WordTitle(getPatientLookupData?.data?.gender) || "-"}
                    </div>
                </div>
                <div className="flex items-center justify-between my-3">
                    <div className="font-medium  text-xs text-[#AAAAAA]">Age:</div>
                    <div className="font-medium text-primary-light text-xs">{getPatientLookupData?.data?.birthDate
                        ? CalculateAge(getPatientLookupData?.data?.birthDate)
                        : "-"}
                      </div>
                </div>
            </div>

        </div>
        <div className="bg-[#FAFAFA] rounded-lg">
          <div className="flex items-center justify-between my-1 mx-2">
               <p className="font-medium  text-xs">Vitals</p>
               <div className="flex font-base">
                      <button className={`flex items-center py-2 px-4 me-2 font-medium rounded-lg bg-white text-xs border border-[#dddddd] ${observationRateLimit ?"cursor-not-allowed text-primary_disable-light": ' text-primary-light'}`} onClick={() => setShowModal(true)} disabled={observationRateLimit}>
                         View History <FaHistory className="ms-2" />
                      </button>
                      <CopyToClipboard
                        text={
                          observation?.length > 0 &&
                          observation
                            ?.map(
                              (item: any) =>
                                `${item?.observationType} : ${formatData(item)} ${
                                  item?.recordedDate ? '('+item?.recordedDate+')' : ''
                                }`
                            )
                            .join("\n") // Join the array with newlines
                        }
                        onCopy={() => {
                          if (observationCopy){
                            handleCopy("vitals")
                          }
                        }} // Handle the copy event
                        options={{
                          message: "Copied to clipboard",
                        }}
                      >
                          <button className={`flex items-center justify-between font-medium  text-xs py-2 px-4 rounded-[8px] bg-white border border-[#dddddd] ${observationCopy ? ' text-primary-light':' cursor-not-allowed text-primary_disable-light' }`}>
                            Copy  to Clipboard
                          {copied === "vitals" ? (
                              <BsCheckCircleFill size={15} className="ms-2" color="green" />
                            ) : (
                              <IoCopyOutline size={15} className="ms-2" />
                            )}
                          </button>
                        
                      </CopyToClipboard>
                </div>
          </div>
          <div className="m-2 p-3 bg-white rounded-lg grid grid-cols-5 gap-4">
            {observationRateLimit ? <ExpiredRateLimit BlockClass={'w-[46.5rem] h-[9rem] '} />:
            (observation ?  (observation?.map((item: any, index: number) => (
              <div className={`${index%5 === 4 || index === observation.length - 1 ? '' : 'border-r-2 border-[#F2F3F3]' } `} key={index}>
                <div className="font-normal text-xs text-[#ABACAD]">{item.observationType}</div>
                <div className="font-medium text-sm text-primary-light">{`${formatData(item)}`}</div>
                <div className="font-normal text-xs text-[#ABACAD]">{item.recordedDate ? item.recordedDate : ' -- '}</div>
                <div className="font-normal text-xs text-[#ABACAD]">Method: {item.method ? item.method : '-'}</div>
              </div>
            ))) : <Skeleton width="46.5rem" height="9rem" className="rounded-lg" />)}
          </div>

        </div>
      </div>

      <div className="mb-4 border-b border-gray-200 dark:border-gray-700 mt-2">
        <ul
          className="flex flex-wrap -mb-px text-sm font-medium text-center"
          role="tablist"
        >
          {["medications", "allergy", "immunization", "notes"].map((tab) => (
            <li className="me-2" role="presentation" key={tab}>
              <button
                className={`inline-block p-4 border-b-2 rounded-t-lg ${
                  activeTab === tab
                    ? "text-[#083B3E] border-[#083B3E] dark:text-blue-500 dark:border-blue-500"
                    : "text-gray-500 hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
                onClick={() => handleTabClick(tab)}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            </li>
          ))}
        </ul>

        <div id="default-styled-tab-content">
          {activeTab === "medications" && (
            <div className="p-4 pt-2 rounded-lg bg-gray-50 dark:bg-gray-800">
              <div className="mt-2 grid grid-cols-12 gap-1">
                <div className="col-span-6 flex items-center">
                  <h6 className="text-[#1C0F03] font-[600] text-[20px]">
                    Medications
                  </h6>
                </div>
                <div className="col-span-6 flex items-center justify-end">
                  <select
                    name="status"
                    id=""
                    className={`mr-[10px] mt-4 w-[8rem] rounded-[8px] h-[35px] bg-white
                border border-[#F2F4F7]  px-2 py-1 focus:outline-none ${medicationsRateLimit ? 'cursor-not-allowed':'cursor-pointer'}
                `}
                    value={status}
                    disabled={medicationsRateLimit}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="discontinued">Discontinued</option>
                    <option value="completed">Completed</option>
                    <option value="all">All</option>
                  </select>
                  {medications?.length > 0 ? (
                    <CopyToClipboard
                      text={medicationClipBoard(medications || [])}
                      onCopy={() =>
                        medications.length > 0
                          ? handleCopy("medications")
                          : null
                      } // Handle the copy event
                      options={{
                        message: "Copied to clipboard",
                      }}
                    >
                      <button
                        className={`mt-4 border border-[#dddddd] text-primary-light bg-white py-2 px-3 rounded-lg
                                    font-[600] text-xs text-center
                                  `}
                      >
                        <span className="flex items-center justify-center">
                          Copy to Clipboard &nbsp;&nbsp;{" "}
                          {copied === "medications" ? (
                            <BsCheckCircleFill size={15} color="green" />
                          ) : (
                            <IoCopyOutline size={15} />
                          )}
                        </span>
                      </button>
                    </CopyToClipboard>
                  ) : (
                    <button
                      className="mt-4 border border-[#dddddd] text-primary_disable-light bg-white py-2 px-3 rounded-lg font-[600] text-xs text-center cursor-not-allowed "
                      disabled={true}
                    >
                      <span className="flex items-center justify-center">
                        Copy to Clipboard &nbsp;&nbsp;{" "}
                        {copied === "medications" ? (
                          <BsCheckCircleFill size={15} color="green" />
                        ) : (
                          <IoCopyOutline size={15} />
                        )}
                      </span>
                    </button>
                  )}
                </div>
                <div className="col-span-12">
                {medicationsRateLimit ? <ExpiredRateLimit BlockClass={'w-[100%] h-[20rem]  '} /> :(<Table
                    loading={loaderMedications}
                    // setSelectedRows={setSelectedRows}
                    hasNextPage={page < totalResults}
                    // selectedRows={selectedRows}
                    data={medications}
                    tableScroll={"100%"}
                    NextPageData={handleNextPage}
                    columns={[
                      "Start Date",
                      "Generic Name",
                      "description",
                      "Strength",
                      "Strength UOM",
                      "Administrations",
                      "directions",
                      "Schedule",
                      "Discontinue Date",
                      // "Narcotic",
                      "End Date",
                      "Status",
                    ]}
                    sort={true}
                    columnColSpans={[1, 1,1,1, 1, 1, 1, 1, 1, 1, 1, 1]}
                    sortTitleKeys={{
                      "Generic Name": "generic",
                      Schedule: "schedule",
                      Status: "status",
                    }}
                    handleSortfunc={(key: any, value: any) =>
                      getMedications(id, true, { [key]: value }, 1)
                    }
                    scopedSlots={{
                      "Start Date": ({ row }: any) => (
                        <td colSpan={1}>
                          <span className="truncate text-[10px] text-ellipsis ">
                            {row?.startDate}
                          </span>
                        </td>
                      ),
                      "Generic Name": ({ row }: any) => (
                        <td colSpan={1} className="truncate  max-w-[20px] text-ellipsis">
                          <span
                            title={row?.generic}
                            className=" text-[12px]"
                          >
                            {row?.generic}
                          </span>
                        </td>
                      ),
                      description: ({ row }: any) => (
                        <td colSpan={1} className="truncate  max-w-[20px] text-ellipsis">
                          <span
                            title={row?.description}
                            className=" text-[12px]"
                          >
                            {row?.description}
                          </span>
                        </td>
                      ),
                      Strength: ({ row }: any) => (
                        <td colSpan={1}>
                          <span
                            title={row?.strength}
                            className="truncate text-[12px] text-ellipsis"
                          >
                            {row?.strength}
                          </span>
                        </td>
                      ),
                      "Strength UOM": ({ row }: any) => (
                        <td colSpan={1}>
                          <span
                            title={row?.strengthUOM}
                            className="truncate text-[12px] text-ellipsis"
                          >
                            {row?.strengthUOM}
                          </span>
                        </td>
                      ),
                      Administrations: ({ row }: any) => (
                        <td colSpan={1}>
                          <span
                            title={
                              row?.administration?.length > 0
                                ? row?.administration.join(",")
                                : "NA"
                            }
                            className="truncate text-[12px] text-ellipsis"
                          >
                            {" "}
                            {row?.administration?.length > 0
                              ? row?.administration.join(",")
                              : "-"}
                          </span>
                        </td>
                      ),
                      Schedule: ({ row }: any) => (
                        <td colSpan={1} className="truncate  max-w-[20px] text-ellipsis">
                          <span
                            title={
                              row?.schedule?.length > 0
                                ? row?.schedule.join(",")
                                : "NA"
                            }
                            className="text-[12px] "
                          >
                            {" "}
                            {row?.schedule?.length > 0
                              ? row?.schedule.join(",")
                              : "NA"}
                          </span>
                        </td>
                      ),
                      directions: ({ row }: any) => (
                        <td colSpan={1} className="truncate  max-w-[20px] text-ellipsis">
                          <span
                            title={row?.directions}
                            className=" text-[12px]  "
                          >
                            {row?.directions}
                          </span>
                        </td>
                      ),
                      "Discontinue Date": ({ row }: any) => (
                        <td colSpan={1}>
                          <span
                            title={row?.discontinueDate}
                            className="truncate text-[12px] text-ellipsis"
                          >
                            {row?.discontinueDate}
                          </span>
                        </td>
                      ),
                      "End Date": ({ row }: any) => (
                        <td colSpan={1}>
                          <span
                            title={row?.endDate}
                            className="truncate text-[12px] text-ellipsis"
                          >
                            {row?.endDate}
                          </span>
                        </td>
                      ),
                      Status: ({ row }: any) => (
                        <td colSpan={1}>
                          <span
                            title={row?.status}
                            className="truncate text-[10px] text-ellipsis"
                          >
                            {row?.status}
                          </span>
                        </td>
                      ),
                    }}
                  />)}
                </div>
              </div>
            </div>
          )}

          {activeTab === "allergy" && (
            <div className="p-4 pt-2 rounded-lg bg-gray-50 dark:bg-gray-800">
              <div className="mt-2 grid grid-cols-12 gap-1">
                <div className="col-span-6 flex items-center">
                  <h6 className="text-[#1C0F03] font-[600] text-[20px]">
                    Allergies
                  </h6>
                </div>
                <div className="col-span-6 flex items-center justify-end">
                  <select
                    name="status"
                    id=""
                    className={`mr-[10px] mt-4 w-[8rem] rounded-[8px] h-[35px] bg-white border border-[#F2F4F7]  px-2 py-1 focus:outline-none ${allergyRateLimit ? 'cursor-not-allowed':'cursor-pointer'}`}
                    value={status}
                    disabled={allergyRateLimit}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                  >
                    <option value="allergy">Allergy</option>
                    <option value="Intolerance">Intolerance</option>
                    <option value="Propensity">Propensity</option>
                    <option value="Unknown">Unknown</option>
                    <option value="all">All</option>
                  </select>
                  <CopyToClipboard
                    text={allergyClipBoard(allergy || [])}
                    onCopy={() =>
                      allergy?.length > 0
                        ? handleCopy("allergy")
                        : null
                    } 
                    options={{
                      message: "Copied to clipboard",
                    }}
                  >
                    <button
                      className={`mt-4 border border-[#dddddd] bg-white py-2 px-3 rounded-lg
                                     font-[600] text-xs text-center ${ allergy?.length > 0 ? ' text-primary-light': ' text-primary_disable-light cursor-not-allowed'}
                                  `}
                    >
                      <span className="flex items-center justify-center">
                        Copy to Clipboard &nbsp;&nbsp;{" "}
                        {copied === "allergy" ? (
                          <BsCheckCircleFill size={15} color="green" />
                        ) : (
                          <IoCopyOutline size={15} />
                        )}
                      </span>
                    </button>
                  </CopyToClipboard>
                </div>
                <div className="col-span-12">
                  <div className="">
                    {allergyRateLimit ? <ExpiredRateLimit BlockClass={'w-[100%] h-[20rem]  '} /> :(
                    <Table
                      loading={loader}
                      data={allergy}
                      tableScroll={"100%"}
                      columns={[
                        "Category",
                        "Type",
                        "Allergen",
                        "Reaction",
                        "Reaction Type Code",
                        "Reaction Sub Type Code",
                        "Severity",
                        "Clinical Status",
                      ]}
                      sort={true}
                      columnColSpans={[3, 3, 3, 3, 3, 3, 3,3]}
                      scopedSlots={{
                        Category: ({ row }: any) => (
                          <td colSpan={3}>
                            <span className="text-[10px]">
                              {row.category ? row.category : "NA"}
                            </span>
                          </td>
                        ),
                        Type: ({ row }: any) => (
                          <td colSpan={3}>
                            <span className="text-[10px]">
                              {row.type ? row.type : "NA"}
                            </span>
                          </td>
                        ),
                        Allergen: ({ row }: any) => (
                          <td colSpan={3}>
                            <span className="text-[10px]">
                              {row.allergen ? row.allergen : "NA"}
                            </span>
                          </td>
                        ),
                        Reaction: ({ row }: any) => (
                          <td colSpan={3} className="truncate  max-w-[20px] text-ellipsis">
                            <span className="text-[10px]" title={row.reaction?.length > 0
                                ? row.reaction.join(",")
                                : "NA"}>
                              {row.reaction?.length > 0
                                ? row.reaction.join(",")
                                : "NA"}
                            </span>
                          </td>
                        ),
                        "Reaction Type Code": ({ row }: any) => (
                          <td colSpan={3} className="truncate  max-w-[20px] text-ellipsis">
                            <span className="text-[10px]" title={row.reactionTypeCode?.length > 0
                                ? row.reactionTypeCode.join(",")
                                : "NA"}>
                              {row.reactionTypeCode?.length > 0
                                ? row.reactionTypeCode.join(",")
                                : "NA"}
                            </span>
                          </td>
                        ),
                        "Reaction Sub Type Code": ({ row }: any) => (
                          <td colSpan={3} className="truncate  max-w-[20px] text-ellipsis">
                            <span className="text-[10px]" title={row.reactionSubTypeCode?.length > 0
                                ? row.reactionSubTypeCode.join(",")
                                : "NA"}>
                              {row.reactionSubTypeCode?.length > 0
                                ? row.reactionSubTypeCode.join(",")
                                : "NA"}
                            </span>
                          </td>
                        ),
                        Severity: ({ row }: any) => (
                          <td colSpan={3}>
                            <span className="text-[10px]">
                              {row.severity ? row.severity : "NA"}
                            </span>
                          </td>
                        ),
                        "Clinical Status": ({ row }: any) => (
                          <td colSpan={3}>
                            <span className="text-[10px]">
                              {row.clinicalStatus ? WordTitle(row.clinicalStatus) : "NA"}
                            </span>
                          </td>
                        ),
                      }}
                    />)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "immunization" && (
            <div className="p-4 pt-2 rounded-lg bg-gray-50 dark:bg-gray-800">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800">
                <div className="mt-2 grid grid-cols-12 gap-1">
                  <div className="col-span-6 flex items-center">
                    <h6 className="text-[#1C0F03] font-[600] text-[20px]">
                      Immunizations
                    </h6>
                  </div>
                  <div className="col-span-6 flex items-center justify-end">
                    <CopyToClipboard
                      text={immuzationClipBoard(immuzations || [])}
                      onCopy={() =>
                        immuzations && immuzations && immuzations?.length > 0
                          ? handleCopy("Immunization")
                          : null
                      } // Handle the copy event
                      options={{
                        message: "Copied to clipboard",
                      }}
                    >
                      <button
                        className={`mt-4 border border-[#dddddd] bg-white py-2 px-3 rounded-lg
                                     font-[600] text-xs text-center ${ immuzations?.length > 0 ? ' text-primary-light': ' text-primary_disable-light cursor-not-allowed'}
                                  `}
                      >
                        <span className="flex items-center justify-center">
                          Copy to Clipboard &nbsp;&nbsp;{" "}
                          {copied === "Immunization" ? (
                            <BsCheckCircleFill size={15} color="green" />
                          ) : (
                            <IoCopyOutline size={15} />
                          )}
                        </span>
                      </button>
                    </CopyToClipboard>
                  </div>
                  <div className="col-span-12">
                    {immuzationsRateLimit ? <ExpiredRateLimit BlockClass={'w-[100%] h-[20rem]  '} /> : (
                    <Table
                      loading={loader}
                      // setSelectedRows={setSelectedRows}
                      // hasNextPage={page < totalResults}
                      // selectedRows={selectedRows}
                      data={immuzations}
                      tableScroll={"100%"}
                      // NextPageData={handleNextPage}
                      columns={[
                        "Consent Confirmed Date",
                        "Immunization",
                        "CVX Description",
                        "Consent Status",
                        "Action",
                      ]}
                      sort={true}
                      columnColSpans={[3, 3, 3,3,1]}
                      sortTitleKeys={
                        {
                          // "Consent Confirmed Date": "consentConfirmedDate",
                          // Immunization: "immunization",
                          // "CVX Description": "cvxDescription",
                        }
                      }
                      // handleSortfunc={(key: any, value: any) => {
                      //   setSort({ [key]: value });
                      //   page == 1
                      //     ? handleUsersData(false, { [key]: value })
                      //     : handleUsersData(false, { [key]: value }, 1);
                      // }}
                      scopedSlots={{
                        "Consent Confirmed Date": ({ row }: any) => (
                          <td colSpan={3}>
                            <span className="text-[10px]">
                              {row.consentConfirmedDate
                                ? row.consentConfirmedDate
                                : "NA"}
                            </span>
                          </td>
                        ),
                        Immunization: ({ row }: any) => (
                          <td colSpan={3}>
                            <span className="text-[10px]">
                              {row.immunization}
                            </span>
                          </td>
                        ),
                        "CVX Description": ({ row }: any) => (
                          <td colSpan={3}>
                            <span className="text-[10px]">
                              {row.cvxDescription}
                            </span>
                          </td>
                        ),
                        "Consent Status": ({ row }: any) => (
                          <td colSpan={3}>
                            <span className="text-[10px]">
                              {row.consentStatus}
                            </span>
                          </td>
                        ),
                        Action: ({ row, key, index }: any) => (
                          <td colSpan={1}>
                            <button
                              className="border border-[#dddddd] text-primary-light bg-white py-1 px-2 rounded-lg
                                          font-[600] text-[10px] text-center"
                              onClick={() => {
                                handleSingleCopy(row, index);
                              }}
                            >
                              <span className="flex items-center justify-center">
                                {singleCopy === index ? (
                                  <BsCheckCircleFill size={15} color="green" />
                                ) : (
                                  <IoCopyOutline size={15} />
                                )}
                              </span>
                            </button>
                          </td>
                        ),
                      }}
                    />)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "notes" && (
            <NotesTab patientId={id} />
          )}
        </div>
      </div>

      <div>
        {showModal && (
          <Popup
            showModal={showModal}
            setShowModal={setShowModal}
            title={"Vitals History"}
            patientId={id}
            // type={selected}
            observation={observation}
            limit={3}
          />
        )}
      </div>
    </div>
  </div>
  );
};

export default Index;
